import { Attr } from '../core/attributes';
import { sanitizeUrlParts, templatizeParts } from '../core/scrub/url';

/** The parts of an OTel Span the instrumentation callbacks are allowed to touch. */
export interface MutableClientSpan {
  name?: string;
  attributes?: Record<string, unknown>;
  setAttribute: (key: string, value: unknown) => void;
  updateName?: (name: string) => unknown;
}

/**
 * Name browser client spans `POST /internal/list/ids-by-profiles`.
 *
 * OTel's XHR and fetch instrumentations name a span after the HTTP method
 * alone, so the live view is a wall of `GET → 200` / `POST → 200` and finding
 * one endpoint means opening spans one at a time to read `http.url`. The server
 * side already reads well because Next names its spans after the route; this
 * gives the browser side the same shape.
 *
 * The path is templatized first — `span_name` is indexed and grouped, so a raw
 * `/company/f000001729377` would both leak an id and split one endpoint into
 * thousands of distinct names.
 *
 * Lives apart from `browser/index.ts` so it can be tested without pulling in
 * `@pydantic/logfire-browser`, which needs a DOM.
 */
export function annotateClientSpan(
  span: MutableClientSpan,
  request: unknown,
  origin?: string,
  captureFullQuery = false,
): void {
  const candidate = request as { url?: string; responseURL?: string } | undefined;
  // fetch hands us a RequestInit with no url, so fall back to the attribute the
  // instrumentation has already set.
  const raw =
    candidate?.url ??
    candidate?.responseURL ??
    (span.attributes?.['http.url'] as string | undefined) ??
    (span.attributes?.['url.full'] as string | undefined);

  if (typeof raw !== 'string' || !raw) return;

  // sanitizeUrlParts, not sanitizeUrl: the parts carry the query-param names and
  // the templated-away ids, both of which are the whole point of looking at one
  // of these spans and neither of which survives the URL itself.
  const parts = sanitizeUrlParts(raw);
  span.setAttribute('url.full', parts.url);
  if (parts.queryKeys) span.setAttribute('url.query_keys', parts.queryKeys);
  if (parts.pathIds) span.setAttribute(Attr.PATH_IDS, parts.pathIds);

  try {
    const base = origin ?? (typeof window === 'undefined' ? 'https://fbl.invalid' : window.location.origin);
    const url = new URL(raw, base);
    span.setAttribute('server.address', url.hostname);

    const method = String(
      span.attributes?.['http.request.method'] ?? span.attributes?.['http.method'] ?? span.name ?? '',
    )
      .trim()
      .toUpperCase();

    span.updateName?.(method ? `${method} ${templatizeParts(url.pathname).path}` : templatizeParts(url.pathname).path);

    // Parity with the server side, where RequestShapeSpanProcessor records the
    // full query outside production. Gated by the caller rather than read from
    // the environment here: there is no process.env in the browser, so
    // configureBrowserTelemetry resolves it once at startup.
    const query = url.search.slice(1);
    if (captureFullQuery && query) span.setAttribute('firmable.debug.query', query.slice(0, 8_000));
  } catch {
    // An unparseable URL is not worth losing the span over.
  }
}
